/**
 * @typedef {Object} ModalConfig
 * @property {string} id - Unique modal identifier
 * @property {string} title - Modal title
 * @property {string} type - Modal type (form, confirmation, dialog)
 * @property {string} [submitText] - Submit button text
 * @property {string} [cancelText] - Cancel button text
 * @property {boolean} [closable] - Whether modal can be closed by clicking outside
 * @property {string} [size] - Modal size (small, medium, large)
 * @property {Object} [defaultData] - Default form data
 * @property {function} [validation] - Validation function
 * @property {Object} [props] - Additional props to pass to modal component
 */

/**
 * @typedef {Object} ShareSuiteModalData
 * @property {Object} testSuite - Test suite object to share
 * @property {string} testSuite.testsuiteid - Test suite ID
 * @property {string} testSuite.testsuitename - Test suite name
 * @property {Object} testSuite.userlist - Current shared users
 * @property {Array} testSuite.userlist.users - Array of shared users
 */

/**
 * @typedef {Object} RunSuiteModalData
 * @property {Object} testSuite - Test suite object to run
 * @property {string} testSuite.testsuiteid - Test suite ID
 * @property {string} testSuite.testsuitename - Test suite name
 * @property {number} testSuite.testcasecount - Number of test cases
 * @property {string} testSuite.emailrecipients - Email recipients
 */

/**
 * @typedef {Object} DeleteSuiteModalData
 * @property {Object} testSuite - Test suite object to delete
 * @property {string} testSuite.testsuiteid - Test suite ID
 * @property {string} testSuite.testsuitename - Test suite name
 */

/**
 * Modal type constants for better intellisense and consistency
 */
export const MODAL_TYPES = {
  FORM: 'form',
  CONFIRMATION: 'confirmation',
  DIALOG: 'dialog',
  ALERT: 'alert',
};

/**
 * Modal size constants
 */
export const MODAL_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra-large',
};

/**
 * Modal ID constants to prevent typos and provide better intellisense
 */
export const MODAL_IDS = {
  SHARE_SUITE: 'SHARE_SUITE',
  RUN_SUITE: 'RUN_SUITE',
  DELETE_SUITE: 'DELETE_SUITE',
  SCHEDULE_SUITE: 'SCHEDULE_SUITE',
};

/**
 * Validation function for run suite modal
 * @param {Object} data - Form data to validate
 * @returns {Object} Validation errors object
 */
const validateRunSuite = (data) => {
  const errors = {};

  if (!data.notificationEmail || !data.notificationEmail.includes('@')) {
    errors.notificationEmail = 'Valid notification email is required';
  }

  // Execution name is optional, but if provided, should have minimum length
  if (data.executionName && data.executionName.length < 3) {
    errors.executionName = 'Execution name must be at least 3 characters';
  }

  return errors;
};

/**
 * Modal configurations object
 * Each modal type has its own configuration defining behavior and validation
 *
 * @type {Object.<string, ModalConfig>}
 */
export const MODAL_CONFIGS = {
  [MODAL_IDS.SHARE_SUITE]: {
    id: MODAL_IDS.SHARE_SUITE,
    title: 'Share test suite',
    type: MODAL_TYPES.FORM,
    size: MODAL_SIZES.MEDIUM,
    submitText: 'Save',
    cancelText: 'Cancel',
    closable: true,
    defaultData: {
      sharedUsers: [],
      emailInput: '',
      selectedRole: '',
    },
    props: {
      // Additional props specific to ShareTestSuiteModal
      showUserLimit: true,
      maxUsers: 50,
    },
  },

  [MODAL_IDS.RUN_SUITE]: {
    id: MODAL_IDS.RUN_SUITE,
    title: 'Run test suite',
    type: MODAL_TYPES.FORM,
    size: MODAL_SIZES.MEDIUM,
    submitText: 'Run',
    cancelText: 'Cancel',
    closable: true,
    validation: validateRunSuite,
    defaultData: {
      executionName: '',
      notifyMe: true,
      notifyOthers: false,
      notificationEmail: '',
      xrayExecutionId: '',
      updateXray: false,
      jiraProjectId: '',
      xrayExecutionIdForJira: '',
    },
    props: {
      // Additional props specific to RunTestSuiteModal
      showJiraIntegration: true,
    },
  },

  [MODAL_IDS.DELETE_SUITE]: {
    id: MODAL_IDS.DELETE_SUITE,
    title: 'Remove test suite',
    type: MODAL_TYPES.CONFIRMATION,
    size: MODAL_SIZES.SMALL,
    submitText: 'Remove test suite',
    cancelText: 'Cancel',
    closable: true,
    defaultData: {},
    props: {
      // Additional props specific to DeleteSuiteModal
      destructive: true,
      message:
        'Proceeding will remove the selected test suite. This action cannot be undone.',
    },
  },

  [MODAL_IDS.SCHEDULE_SUITE]: {
    id: MODAL_IDS.SCHEDULE_SUITE,
    title: 'Schedule test suite',
    type: MODAL_TYPES.FORM,
    size: MODAL_SIZES.LARGE,
    submitText: 'Save Schedule',
    cancelText: 'Cancel',
    closable: true,
    defaultData: {
      cronExpression: '',
      startDate: '',
      endDate: '',
      disabled: false,
      updateXray: false,
      jiraProjectId: '',
      jiraTestExecutionId: '',
    },
    props: {
      // Additional props specific to ScheduleModal
      showAdvancedOptions: true,
    },
  },
};

/**
 * Helper function to get modal configuration by ID
 * @param {string} modalId - Modal ID
 * @returns {ModalConfig|null} Modal configuration or null if not found
 */
export const getModalConfig = (modalId) => {
  return MODAL_CONFIGS[modalId] || null;
};

/**
 * Helper function to validate modal data
 * @param {string} modalId - Modal ID
 * @param {Object} data - Data to validate
 * @returns {Object} Validation errors
 */
export const validateModalData = (modalId, data) => {
  const config = getModalConfig(modalId);
  if (!config || !config.validation) {
    return {};
  }

  try {
    return config.validation(data);
  } catch (error) {
    console.error(`Validation error for modal ${modalId}:`, error);
    return { general: 'Validation error occurred' };
  }
};

/**
 * Helper function to get default data for a modal
 * @param {string} modalId - Modal ID
 * @returns {Object} Default data object
 */
export const getModalDefaultData = (modalId) => {
  const config = getModalConfig(modalId);
  return config?.defaultData || {};
};
